/** Grading scales + a curated world-university list (Phase G).
 *  The scale decides pass/fail on the term-end report; "other" universities
 *  fall back to a manual country-scale pick. */

export interface GradingScale {
  id: "de" | "ir20" | "us100" | "uk100" | "tr100" | "fr20" | "nl10";
  /** 1..10 — higher = stricter display scale (informational). */
  label: string;
  /** Returns true when the numeric grade PASSES on this scale. */
  passes: (grade: number) => boolean;
  /** How to phrase the grade (decimal comma cultures etc.). */
  format: (grade: number) => string;
}

export const GRADING_SCALES: Record<GradingScale["id"], GradingScale> = {
  de: { id: "de", label: "1.0–5.0 (DE/AT)", passes: (g) => g > 0 && g <= 4.0, format: (g) => g.toFixed(1) },
  ir20: { id: "ir20", label: "0–20 (IR)", passes: (g) => g >= 10, format: (g) => g.toFixed(2) },
  fr20: { id: "fr20", label: "0–20 (FR)", passes: (g) => g >= 10, format: (g) => g.toFixed(2) },
  us100: { id: "us100", label: "0–100 (US)", passes: (g) => g >= 60, format: (g) => String(Math.round(g)) },
  uk100: { id: "uk100", label: "0–100 (UK)", passes: (g) => g >= 40, format: (g) => String(Math.round(g)) },
  tr100: { id: "tr100", label: "0–100 (TR)", passes: (g) => g >= 50, format: (g) => String(Math.round(g)) },
  nl10: { id: "nl10", label: "1–10 (NL)", passes: (g) => g >= 5.5, format: (g) => g.toFixed(1) }
};

export interface UniversityEntry {
  name: string;
  country: string;
  scale: GradingScale["id"];
}

export const UNIVERSITIES: UniversityEntry[] = [
  { name: "Hochschule RheinMain", country: "DE", scale: "de" },
  { name: "Goethe-Universität Frankfurt", country: "DE", scale: "de" },
  { name: "TU Darmstadt", country: "DE", scale: "de" },
  { name: "TU München", country: "DE", scale: "de" },
  { name: "RWTH Aachen", country: "DE", scale: "de" },
  { name: "Universität Heidelberg", country: "DE", scale: "de" },
  { name: "HU Berlin", country: "DE", scale: "de" },
  { name: "FU Berlin", country: "DE", scale: "de" },
  { name: "TU Berlin", country: "DE", scale: "de" },
  { name: "Universität Hamburg", country: "DE", scale: "de" },
  { name: "Universität Stuttgart", country: "DE", scale: "de" },
  { name: "Karlsruher Institut für Technologie", country: "DE", scale: "de" },
  { name: "Universität Köln", country: "DE", scale: "de" },
  { name: "Westfälische Wilhelms-Universität Münster", country: "DE", scale: "de" },
  { name: "Universität Würzburg", country: "DE", scale: "de" },
  { name: "Leibniz Universität Hannover", country: "DE", scale: "de" },
  { name: "TU Dresden", country: "DE", scale: "de" },
  { name: "Universität Leipzig", country: "DE", scale: "de" },
  { name: "Universität Bonn", country: "DE", scale: "de" },
  { name: "Universität Mannheim", country: "DE", scale: "de" },
  { name: "Universität Wien", country: "AT", scale: "de" },
  { name: "TU Wien", country: "AT", scale: "de" },
  { name: "ETH Zürich", country: "CH", scale: "de" },
  { name: "Universität Zürich", country: "CH", scale: "de" },
  { name: "TU Delft", country: "NL", scale: "nl10" },
  { name: "Universiteit van Amsterdam", country: "NL", scale: "nl10" },
  { name: "KU Leuven", country: "BE", scale: "nl10" },
  { name: "Sorbonne Université", country: "FR", scale: "fr20" },
  { name: "Université Paris-Saclay", country: "FR", scale: "fr20" },
  { name: "Université de Lyon", country: "FR", scale: "fr20" },
  { name: "Università di Bologna", country: "IT", scale: "fr20" },
  { name: "Sapienza Università di Roma", country: "IT", scale: "fr20" },
  { name: "Politecnico di Milano", country: "IT", scale: "fr20" },
  { name: "Universidad Complutense de Madrid", country: "ES", scale: "nl10" },
  { name: "Universitat de Barcelona", country: "ES", scale: "nl10" },
  { name: "University of Oxford", country: "GB", scale: "uk100" },
  { name: "University of Cambridge", country: "GB", scale: "uk100" },
  { name: "Imperial College London", country: "GB", scale: "uk100" },
  { name: "UCL", country: "GB", scale: "uk100" },
  { name: "University of Edinburgh", country: "GB", scale: "uk100" },
  { name: "MIT", country: "US", scale: "us100" },
  { name: "Harvard University", country: "US", scale: "us100" },
  { name: "Stanford University", country: "US", scale: "us100" },
  { name: "UC Berkeley", country: "US", scale: "us100" },
  { name: "University of Toronto", country: "CA", scale: "us100" },
  { name: "University of Melbourne", country: "AU", scale: "us100" },
  { name: "Middle East Technical University", country: "TR", scale: "tr100" },
  { name: "Istanbul Technical University", country: "TR", scale: "tr100" },
  { name: "Boğaziçi University", country: "TR", scale: "tr100" },
  { name: "Istanbul University", country: "TR", scale: "tr100" },
  { name: "Ankara Üniversitesi", country: "TR", scale: "tr100" },
  { name: "دانشگاه تهران", country: "IR", scale: "ir20" },
  { name: "دانشگاه صنعتی شریف", country: "IR", scale: "ir20" },
  { name: "دانشگاه امیرکبیر", country: "IR", scale: "ir20" },
  { name: "دانشگاه علم و صنعت", country: "IR", scale: "ir20" },
  { name: "دانشگاه خواجه نصیر", country: "IR", scale: "ir20" },
  { name: "دانشگاه شهید بهشتی", country: "IR", scale: "ir20" },
  { name: "دانشگاه فردوسی مشهد", country: "IR", scale: "ir20" },
  { name: "دانشگاه اصفهان", country: "IR", scale: "ir20" },
  { name: "دانشگاه تبریز", country: "IR", scale: "ir20" },
  { name: "دانشگاه شیراز", country: "IR", scale: "ir20" },
  { name: "دانشگاه علامه طباطبایی", country: "IR", scale: "ir20" },
  { name: "دانشگاه الزهرا", country: "IR", scale: "ir20" },
  { name: "دانشگاه صنعتی اصفهان", country: "IR", scale: "ir20" },
  { name: "دانشگاه علوم پزشکی تهران", country: "IR", scale: "ir20" },
  { name: "دانشگاه آزاد اسلامی", country: "IR", scale: "ir20" },
  { name: "دانشگاه پیام نور", country: "IR", scale: "ir20" }
];

/** Italian/Dutch/Spanish scales ride their 30ths/10ths — map to closest app scale. */
export function scaleFor(universityName: string): GradingScale {
  const entry = UNIVERSITIES.find((u) => u.name === universityName);
  if (!entry) return GRADING_SCALES.us100;
  return GRADING_SCALES[entry.scale];
}

/** Country term/credit systems — the "intelligent defaults" behind the
 *  academic-path card: pick a country (or a university from it) and the app
 *  knows how terms and credits are normally structured there. */
export interface CountrySystem {
  termsPerYear: number;
  creditsPerTerm: number;
  totalBachelor: number;
  /** Local term naming hint shown in the smart row. */
  termsLabel: string;
}

export const COUNTRY_SYSTEMS: Record<string, CountrySystem> = {
  DE: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "WiSe/SoSe" },
  AT: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "WS/SS" },
  CH: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "HS/FS" },
  NL: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "sem 1/2" },
  BE: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "sem 1/2" },
  FR: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "S1/S2" },
  IT: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 180, termsLabel: "S1/S2" },
  ES: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 240, termsLabel: "S1/S2" },
  GB: { termsPerYear: 2, creditsPerTerm: 60, totalBachelor: 360, termsLabel: "Autumn/Spring" },
  US: { termsPerYear: 2, creditsPerTerm: 15, totalBachelor: 120, termsLabel: "Fall/Spring" },
  CA: { termsPerYear: 2, creditsPerTerm: 15, totalBachelor: 120, termsLabel: "Fall/Winter" },
  AU: { termsPerYear: 2, creditsPerTerm: 12, totalBachelor: 144, termsLabel: "Sem 1/2" },
  TR: { termsPerYear: 2, creditsPerTerm: 30, totalBachelor: 240, termsLabel: "Güz/Bahar" },
  IR: { termsPerYear: 2, creditsPerTerm: 20, totalBachelor: 140, termsLabel: "نیمسال اول/دوم" },
};

export const KNOWN_COUNTRIES = Object.keys(COUNTRY_SYSTEMS);

export function systemFor(country: string): CountrySystem | null {
  return COUNTRY_SYSTEMS[country] ?? null;
}

/** Country of the picked university ('' when unknown). */
export function countryOf(universityName: string): string {
  return UNIVERSITIES.find((u) => u.name === universityName)?.country ?? "";
}
